import { Schedule, Scale } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
    extractJobColorPreviews,
    extractJobFilamentType,
    getJobDisplaySpecs,
    resolveJobColorPreview,
} from '../../helpers/jobDisplay';

function hexLuminance(hex) {
    const raw = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
        return 1;
    }
    const r = parseInt(raw.slice(0, 2), 16) / 255;
    const g = parseInt(raw.slice(2, 4), 16) / 255;
    const b = parseInt(raw.slice(4, 6), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

function contrastText(hex) {
    return hexLuminance(hex) > 0.55 ? '#111111' : '#FFFFFF';
}

function MarqueeText({ text, color }) {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [overflow, setOverflow] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        const label = textRef.current;
        if (!container || !label) {
            return undefined;
        }
        const measure = () => {
            setOverflow(label.scrollWidth > container.clientWidth + 1);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        observer?.observe(container);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [text]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                overflow: 'hidden',
                px: 0.4,
            }}
        >
            <Typography
                ref={textRef}
                component="span"
                sx={{
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color,
                    ...(overflow ? {
                        animation: 'jobFilamentMarquee 4.5s linear infinite',
                        '@keyframes jobFilamentMarquee': {
                            '0%, 12%': { transform: 'translateX(0)' },
                            '88%, 100%': { transform: 'translateX(calc(-100% + 28px))' },
                        },
                    } : {}),
                }}
            >
                {text}
            </Typography>
        </Box>
    );
}

function FilamentTypeCircle({ type, color, title }) {
    const bg = color || '#999999';
    const fg = contrastText(bg);
    return (
        <Box
            title={title || type}
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.18)',
                bgcolor: bg,
                color: fg,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            <MarqueeText text={type || '?'} color={fg} />
        </Box>
    );
}

/** Circles for job filaments — same look as printer slots, display-only. */
export function getJobFilamentCircles(job) {
    const fallbackType = extractJobFilamentType(job) || '?';
    const linked = (job?.filaments || [])
        .map((filament, index) => {
            const preview = resolveJobColorPreview(filament?.color)
                || resolveJobColorPreview(filament?.color_label);
            const type = String(filament?.type || '').trim() || fallbackType;
            if (!preview && !String(filament?.type || '').trim()) {
                return null;
            }
            return {
                key: `${filament?.filament_uuid || index}-${type}-${preview?.css || 'none'}`,
                type,
                color: preview?.css === 'transparent' ? '#e5e7eb' : (preview?.css || '#999999'),
                title: [type, filament?.color_label || filament?.color, filament?.brand]
                    .filter(Boolean)
                    .join(' · '),
            };
        })
        .filter(Boolean);

    if (linked.length > 0) {
        return linked;
    }

    return extractJobColorPreviews(job).map((preview, index) => ({
        key: `meta-${preview.css}-${index}`,
        type: fallbackType,
        color: preview.css === 'transparent' ? '#e5e7eb' : preview.css,
        title: [fallbackType, preview.label].filter(Boolean).join(' · '),
    }));
}

export default function JobListSpecs({ job, compact = false }) {
    const specs = getJobDisplaySpecs(job);
    const filamentCircles = getJobFilamentCircles(job);

    if (!specs.hasSpecs && filamentCircles.length === 0) {
        return null;
    }

    return (
        <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            useFlexGap
            spacing={compact ? 0.5 : 0.75}
            sx={{ mt: compact ? 0.5 : 0.75 }}
        >
            {specs.durationLabel && (
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<Schedule sx={{ fontSize: '0.95rem !important' }} />}
                    label={specs.durationLabel}
                    sx={{ height: 24, '& .MuiChip-label': { px: 0.75 } }}
                />
            )}
            {specs.filamentLabel && (
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<Scale sx={{ fontSize: '0.95rem !important' }} />}
                    label={specs.filamentLabel}
                    sx={{ height: 24, '& .MuiChip-label': { px: 0.75 } }}
                />
            )}
            {filamentCircles.length > 0 && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 0.25 }}>
                    {!compact && (
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
                            Filamento(s):
                        </Typography>
                    )}
                    {filamentCircles.map((item) => (
                        <FilamentTypeCircle
                            key={item.key}
                            type={item.type}
                            color={item.color}
                            title={item.title}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
